/**
 * Wire contract between the website wizard and the two callables
 * (createCompanyRegistrationDraft, submitCompanyRegistration).
 *
 * Flow: draft (gets an id, a token and one signed upload URL per file slot)
 * → browser PUTs each file to its URL → submit (payload with the Storage
 * paths) → function validates, stores, builds PDFs, emails → success.
 */
import type { FieldErrors, RegistrationPayload } from './validation';

/** One file the browser will upload. */
export interface UploadSlotRequest {
  /** `people.<i>.idFront` | `people.<i>.idBack` | `people.<i>.signature` | `declaration.signature` */
  slot: string;
  contentType: string;
  /** Bytes, after client-side compression. */
  size: number;
}

export interface CreateDraftRequest {
  slots: UploadSlotRequest[];
  /** Honeypot — real users never fill it. */
  website?: string;
  /** When the visitor opened the form (ms). Bots submit instantly. */
  startedAtMs?: number;
}

export interface UploadTarget {
  slot: string;
  /** Storage path to put back into the payload. */
  path: string;
  /** Signed V4 PUT URL (valid ~30 min). */
  url: string;
  /** Headers the PUT MUST send exactly (Content-Type + size-range guard). */
  headers: Record<string, string>;
}

export interface CreateDraftResponse {
  id: string;
  token: string;
  uploads: UploadTarget[];
}

export interface SubmitRequest {
  id: string;
  token: string;
  payload: RegistrationPayload;
  website?: string;
}

export type SubmitResponse =
  | { ok: true; reference: string; status: 'emailed' | 'received' }
  | { ok: false; fieldErrors: FieldErrors };

export const UPLOAD_SLOT_RE = /^(people\.(\d{1,2})\.(idFront|idBack|signature)|declaration\.signature)$/;

/** Max bytes per file the signed URL accepts (matches MAX_UPLOAD_BYTES). */
export const SIGNED_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

/** localStorage entry names shared by the calculator and the wizard. */
export const LS_WIZARD_STORE = 'pi.familyPty.registration.v1';
export const LS_CALCULATOR_STORE = 'pi.familyPty.calculator.v1';

/** What the calculator hands the wizard's People step. */
export interface CalculatorHandoff {
  people: { name: string; shareholdingPct: number }[];
  savedAtMs: number;
}
